import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw, Search } from 'lucide-react';

import SimulationResultsContent from '../../simulationResults/SimulationResultsContent';
import ClassificationCard from './ClassificationCard';
import RegroundPanel from './RegroundPanel';
import SimulationList from './SimulationList';
import ChatTranscript from './ChatTranscript';
import {
  BUCKET_UNCLASSIFIED,
  fetchClassifications,
  fetchSimulations,
  fetchUseCaseOptions,
  requestReclassify,
  requestReground,
  type SimulationDetailResponse,
  type ItemKind,
  type SimulationRow,
  type SortMode,
} from './api';
import {
  PAGE_SIZE,
  RUN_POLL_DEADLINE_MS,
  RUN_POLL_INTERVAL_MS,
  SEARCH_DEBOUNCE_MS,
  asWhen,
} from './constants';

/**
 * Admin › Report Quality — browse every simulation and see how its report scored.
 *
 * Browsing is the primary mode and the worst-first sort is a view of it, not the other way round:
 * the classifier is explicitly "not a gate" (QWK 0.429), so its ranking is a hint about where to
 * look, not a work queue that should hide everything else.
 *
 * The report itself is rendered by the product's own results view, so an admin judges exactly what
 * a customer sees rather than a second, drifting rendition of it.
 */

const SORTS: Array<{ value: SortMode; label: string }> = [
  { value: 'recent', label: 'Newest' },
  { value: 'priority', label: 'Needs review' },
];

const KINDS: Array<{ value: ItemKind | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'simulation', label: 'Simulations' },
  { value: 'chat', label: 'Chats' },
];

const BUCKET_OPTIONS = [
  { value: '', label: 'All quality' },
  { value: 'low', label: 'Low' },
  { value: 'mid', label: 'Mid' },
  { value: 'high', label: 'High' },
  { value: BUCKET_UNCLASSIFIED, label: 'Not classified' },
];

const ReportQualityTab: React.FC = () => {
  const [rows, setRows] = useState<SimulationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [useCase, setUseCase] = useState('');
  const [bucket, setBucket] = useState('');
  const [sort, setSort] = useState<SortMode>('recent');
  const [kind, setKind] = useState<ItemKind | ''>('');

  const [useCases, setUseCases] = useState<string[]>([]);
  const [unreliable, setUnreliable] = useState<string[]>([]);

  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [detail, setDetail] = useState<SimulationDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [regrounding, setRegrounding] = useState(false);

  // Selection is only auto-set on first load. Re-filtering must not yank the pane away from the
  // simulation the admin is reading.
  const hasAutoSelected = useRef(false);

  // Which item the detail pane is currently FOR. Every async write to `detail` checks this first,
  // so a response for an item the admin has already left is dropped instead of replacing what
  // they are looking at. Without it a slow fetch — or the delayed re-classify refresh — could
  // swap a chat transcript for a simulation report under a header still naming the chat.
  const activeRef = useRef<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Note the absence of a `setListLoading(true)` here: setting state synchronously inside an
  // effect triggers a cascading render (and the lint rule that catches it). The spinner is turned
  // ON by whichever handler changed a filter, and OFF here when the answer arrives.
  const loadList = useCallback(async () => {
    try {
      const body = await fetchSimulations({
        search: debouncedSearch || undefined,
        useCase: useCase || undefined,
        bucket: bucket || undefined,
        kind: kind || undefined,
        sort,
        limit: PAGE_SIZE,
        offset: 0,
      });
      setRows(body.simulations);
      setTotal(body.total);
      setError(null);
      if (!hasAutoSelected.current && body.simulations.length > 0) {
        hasAutoSelected.current = true;
        activeRef.current = body.simulations[0].item_ref;
        setDetailLoading(true);
        setSelectedRef(body.simulations[0].item_ref);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load simulations');
    } finally {
      setListLoading(false);
    }
  }, [debouncedSearch, useCase, bucket, kind, sort]);

  useEffect(() => { void (async () => { await loadList(); })(); }, [loadList]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const options = await fetchUseCaseOptions();
        if (cancelled) return;
        setUseCases(options.use_cases);
        setUnreliable(options.unreliable);
      } catch {
        /* filter options are a convenience; the list still works without them */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /** Same rule as loadList: the spinner is switched on by the selection handler, off here. */
  const loadDetail = useCallback(async (itemRef: string) => {
    try {
      const body = await fetchClassifications(itemRef);
      if (activeRef.current !== itemRef) return;   // the admin moved on; this answer is stale
      setDetail(body);
    } catch (e) {
      if (activeRef.current !== itemRef) return;
      setDetail(null);
      setError(e instanceof Error ? e.message : 'Failed to load the classification');
    } finally {
      if (activeRef.current === itemRef) setDetailLoading(false);
    }
  }, []);

  const onSelect = (itemRef: string) => {
    activeRef.current = itemRef;
    // Clear the previous item's detail: `selected` prefers `detail` over the list row, so leaving
    // it set would show the OLD item's header and card until the new fetch lands.
    setDetail(null);
    // The busy flags belong to the item that was acted on, not to the pane.
    setReclassifying(false);
    setRegrounding(false);
    setDetailLoading(true);
    setSelectedRef(itemRef);
  };

  /** Every filter change re-runs the list query, so each shows the spinner as it sets its value. */
  const withSpinner = <T,>(set: (value: T) => void) => (value: T) => {
    setListLoading(true);
    set(value);
  };

  useEffect(() => {
    if (selectedRef == null) return;
    void (async () => { await loadDetail(selectedRef); })();
  }, [selectedRef, loadDetail]);

  /**
   * Wait for a NEW run to land, rather than guessing when one finished.
   *
   * This replaced a fixed 2.5-second timeout plus a single refetch. That was already optimistic
   * for a classification, and hopeless for a grounding audit — measured at four to twelve minutes.
   * So nothing is shown as finished until a row with an id we have not seen actually arrives.
   *
   * Bails the moment the admin moves to another item: a result belonging to what they left must
   * never replace what they are now reading.
   */
  const pollForNewRun = useCallback(async (itemRef: string, knownLatestId: number | null) => {
    const deadline = Date.now() + RUN_POLL_DEADLINE_MS;

    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, RUN_POLL_INTERVAL_MS));
      if (activeRef.current !== itemRef) return;

      try {
        const body = await fetchClassifications(itemRef);
        if (activeRef.current !== itemRef) return;
        if ((body.classifications?.[0]?.id ?? null) !== knownLatestId) {
          setDetail(body);
          void loadList();
          return;
        }
      } catch {
        /* a failed poll is not a failed run — keep waiting for the worker */
      }
    }

    setError('The run is taking longer than expected. Reopen this item to check for a result.');
  }, [loadList]);

  /** Queue a run, then wait for it. `start` names the action so the two buttons share everything
   *  except the call they make and the flag they hold. */
  const runAndWait = async (
    start: (itemRef: string) => Promise<unknown>,
    setBusy: (busy: boolean) => void,
    failure: string,
  ) => {
    const itemRef = selectedRef;
    if (itemRef == null) return;
    const knownLatestId = detail?.classifications?.[0]?.id ?? null;

    setBusy(true);
    try {
      await start(itemRef);
      await pollForNewRun(itemRef, knownLatestId);
    } catch (e) {
      setError(e instanceof Error ? e.message : failure);
    } finally {
      if (activeRef.current === itemRef) setBusy(false);
    }
  };

  const onReclassify = () =>
    runAndWait(requestReclassify, setReclassifying, 'Could not queue the classification');

  const onReground = () =>
    runAndWait(requestReground, setRegrounding, 'Could not queue the grounding audit');

  const selected = detail?.simulation ?? rows.find((r) => r.item_ref === selectedRef) ?? null;
  const latest = detail?.classifications?.[0] ?? null;
  const isUnreliable = useMemo(
    () => !!selected?.use_case && unreliable.includes(selected.use_case),
    [selected, unreliable],
  );

  if (error && rows.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-600">{error}</p>
        <button
          type="button"
          onClick={() => void loadList()}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-gray-300
                     bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:border-blue-500"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="-m-6">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-gray-200 bg-gray-50
                      px-5 py-4">
        <div className="relative min-w-[200px] max-w-[320px] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => withSpinner(setSearch)(e.target.value)}
            placeholder="Search by name or id…"
            className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-[13px]
                       focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <select
          aria-label="Use case"
          value={useCase}
          onChange={(e) => withSpinner(setUseCase)(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-[13px] text-gray-700"
        >
          <option value="">All use cases</option>
          {useCases.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>

        <select
          aria-label="Quality"
          value={bucket}
          onChange={(e) => withSpinner(setBucket)(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-[13px] text-gray-700"
        >
          {BUCKET_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <div className="flex rounded-lg bg-gray-100 p-0.5">
          {KINDS.map((option) => (
            <button
              key={option.value || 'all'}
              type="button"
              onClick={() => withSpinner(setKind)(option.value)}
              aria-pressed={kind === option.value}
              className={`rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
                kind === option.value
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex rounded-lg bg-gray-100 p-0.5">
          {SORTS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => withSpinner(setSort)(option.value)}
              aria-pressed={sort === option.value}
              className={`rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
                sort === option.value
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <span className="ml-auto text-[12.5px] text-gray-500">
          {total.toLocaleString('en-US')} item{total === 1 ? '' : 's'}
          {listLoading && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" aria-hidden="true" />}
        </span>
      </div>

      <div className="flex min-h-[600px] flex-col md:flex-row">
        <div className="max-h-[78vh] w-full shrink-0 overflow-y-auto border-b border-gray-200
                        md:w-[360px] md:border-b-0 md:border-r">
          <SimulationList
            simulations={rows}
            selectedRef={selectedRef}
            loading={listLoading}
            onSelect={onSelect}
          />
        </div>

        <div className="max-h-[78vh] min-w-0 flex-1 overflow-y-auto bg-gray-50">
          {selected == null ? (
            <p className="px-6 py-16 text-center text-sm text-gray-500">
              Select an item to see its report or conversation, and how it scored.
            </p>
          ) : (
            <>
              <header className="border-b border-gray-200 bg-white px-6 py-4">
                <h2 className="text-[17px] font-bold tracking-tight text-gray-900">
                  {selected.name
                    || (selected.kind === 'chat' ? `Chat ${selected.id}` : `Simulation ${selected.id}`)}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12.5px] text-gray-500">
                  <span>{selected.kind === 'chat' ? 'Chat session' : 'Simulation'} #{selected.id}</span>
                  <span aria-hidden="true">·</span>
                  <span>{selected.use_case || 'no use case'}</span>
                  <span aria-hidden="true">·</span>
                  <span>
                    {selected.kind === 'chat' ? 'Started' : 'Completed'} {asWhen(selected.created_at)}
                  </span>
                </div>
              </header>

              {detailLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
              ) : (
                <>
                  <ClassificationCard
                    classification={latest}
                    unreliableUseCase={isUnreliable}
                    busy={reclassifying}
                    onReclassify={() => void onReclassify()}
                    regroundEligible={
                      selected.kind === 'chat' ? undefined : (selected.reground_eligible ?? false)
                    }
                    regrounding={regrounding}
                    onReground={() => void onReground()}
                  />
                  <RegroundPanel reground={latest?.reground ?? null} running={regrounding} />
                </>
              )}

              <section className="m-6 rounded-xl border border-gray-200 bg-white">
                <h3 className="border-b border-gray-200 px-5 py-3 text-[11px] font-bold uppercase
                               tracking-wide text-gray-400">
                  {selected.kind === 'chat'
                    ? 'The conversation, exactly as the customer saw it'
                    : 'The report, exactly as the customer sees it'}
                </h3>
                {selected.kind === 'chat' ? (
                  <ChatTranscript key={selected.item_ref} sessionId={selected.id} />
                ) : (
                  <SimulationResultsContent
                    key={selected.item_ref}
                    simulationId={selected.id}
                    isAdminView
                    hideChat
                    hideHeader
                  />
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportQualityTab;
